import { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router'
import { demoApiService } from '../services/demoApiService'
import { usePreviewRender } from '../hooks/usePreviewRender'
import { useIsMobile } from '../hooks/useMediaQuery'
import type { DemoTemplate, TemplateVariable, PayloadFormat } from '../types'
import { buildJsonSkeleton } from '../utils/payloadSkeleton'
import PayloadEditor from '../components/PayloadEditor'
import PreviewPane from '../components/PreviewPane'
import { colors, fontFamily, fontSize, radius, shadow, spacing } from '../styles/theme'

export default function DemoPage() {
  const navigate = useNavigate()
  const isMobile = useIsMobile()

  const [templates, setTemplates] = useState<DemoTemplate[]>([])
  const [selected, setSelected] = useState<DemoTemplate | null>(null)
  const [variables, setVariables] = useState<TemplateVariable[]>([])
  const [loadingTemplates, setLoadingTemplates] = useState(true)
  const [loadingVars, setLoadingVars] = useState(false)
  const [error, setError] = useState('')
  const [activeTab, setActiveTab] = useState<'editor' | 'preview'>('editor')

  const previewFn = useCallback(
    (payload: string, format: 'json' | 'xml') =>
      demoApiService.preview(selected!.currentVersionId!, payload, format),
    [selected]
  )

  const { pdfUrl, loading: pdfLoading, error: pdfError, trigger } = usePreviewRender(
    selected?.currentVersionId ?? null,
    previewFn
  )

  useEffect(() => {
    demoApiService.templates()
      .then(t => {
        setTemplates(t)
        if (t.length > 0) setSelected(t[0])
      })
      .catch(() => setError('Failed to load demo templates'))
      .finally(() => setLoadingTemplates(false))
  }, [])

  useEffect(() => {
    if (!selected?.currentVersionId) return
    setLoadingVars(true)
    demoApiService.variables(selected.currentVersionId)
      .then(setVariables)
      .catch(() => setVariables([]))
      .finally(() => setLoadingVars(false))
  }, [selected]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleRender = useCallback((payload: string, format: PayloadFormat) => {
    trigger(payload, format)
    if (isMobile) setActiveTab('preview')
  }, [trigger, isMobile])

  return (
      <div style={{ fontFamily: fontFamily.sans, padding: spacing.lg, display: 'flex', flexDirection: 'column', gap: spacing.lg, maxWidth: 1400, width: '100%', margin: '0 auto', boxSizing: 'border-box', minHeight: 'calc(100vh - 60px)' }}>
        {/* Hero */}
        <div style={{ textAlign: 'center', paddingTop: spacing.md }}>
          <h1 style={{ margin: 0, fontSize: isMobile ? fontSize.xl : fontSize.xxl, fontWeight: 800, color: colors.text, fontFamily: fontFamily.sans }}>
            Fill a template, get a PDF — instantly.
          </h1>
          <p style={{ margin: `${spacing.sm} 0 0`, color: colors.textSecondary, fontSize: fontSize.md }}>
            Pick a sample template, fill in the values, and watch the document update in real time.
          </p>
        </div>

        {/* Template picker */}
        {loadingTemplates ? (
          <div style={{ color: colors.textSecondary, textAlign: 'center' }}>Loading templates…</div>
        ) : error ? (
          <div style={{ color: colors.error, textAlign: 'center' }}>{error}</div>
        ) : (
          <div style={{
            display: 'flex',
            gap: spacing.md,
            flexWrap: 'wrap',
            justifyContent: 'center',
          }}>
            {templates.map(t => (
              <button
                key={t.templateId}
                onClick={() => setSelected(t)}
                style={{
                  padding: `${spacing.sm} ${spacing.lg}`,
                  background: selected?.templateId === t.templateId ? colors.primary : colors.surface,
                  color: selected?.templateId === t.templateId ? colors.onPrimary : colors.text,
                  border: `2px solid ${selected?.templateId === t.templateId ? colors.primary : colors.border}`,
                  borderRadius: radius.md,
                  fontSize: fontSize.base,
                  fontWeight: selected?.templateId === t.templateId ? 600 : 400,
                  cursor: 'pointer',
                  boxShadow: shadow.sm,
                  transition: 'all 0.15s',
                }}
              >
                <div style={{ fontWeight: 600 }}>{t.name}</div>
                {t.description && (
                  <div style={{ fontSize: fontSize.sm, opacity: 0.75, marginTop: '2px' }}>{t.description}</div>
                )}
              </button>
            ))}
          </div>
        )}

        {/* Mobile tab bar */}
        {selected && isMobile && (
          <div style={{ display: 'flex', border: `1px solid ${colors.border}`, borderRadius: radius.md, overflow: 'hidden' }}>
            {(['editor', 'preview'] as const).map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                style={{
                  flex: 1,
                  padding: spacing.sm,
                  border: 'none',
                  background: activeTab === tab ? colors.primary : colors.surface,
                  color: activeTab === tab ? colors.onPrimary : colors.textSecondary,
                  fontWeight: activeTab === tab ? 600 : 400,
                  fontSize: fontSize.base,
                  cursor: 'pointer',
                  textTransform: 'capitalize',
                }}
              >
                {tab}
              </button>
            ))}
          </div>
        )}

        {/* Editor + Preview */}
        {selected && (
          <div style={{
            flex: 1,
            display: isMobile ? 'block' : 'grid',
            gridTemplateColumns: isMobile ? undefined : '380px 1fr',
            gap: spacing.lg,
            minHeight: isMobile ? undefined : 600,
          }}>
            {/* Editor */}
            <div style={{
              display: isMobile && activeTab !== 'editor' ? 'none' : 'flex',
              flexDirection: 'column',
              gap: spacing.md,
            }}>
              <div style={{
                background: colors.surface,
                borderRadius: radius.md,
                padding: spacing.lg,
                boxShadow: shadow.sm,
              }}>
                {loadingVars ? (
                  <div style={{ color: colors.textSecondary, fontSize: fontSize.sm }}>Loading variables…</div>
                ) : (
                  <PayloadEditor variables={variables} onChange={() => {}} onRender={handleRender} renderLoading={pdfLoading} />
                )}
              </div>
            </div>

            {/* Preview */}
            <div style={{
              display: isMobile && activeTab !== 'preview' ? 'none' : 'flex',
              flexDirection: 'column',
              minHeight: isMobile ? 500 : 600,
            }}>
              <PreviewPane pdfUrl={pdfUrl} loading={pdfLoading} error={pdfError} />
            </div>
          </div>
        )}

        {/* CTA */}
        <div style={{
          textAlign: 'center',
          padding: `${spacing.xl} 0`,
          borderTop: `1px solid ${colors.border}`,
        }}>
          <p style={{ margin: `0 0 ${spacing.md}`, color: colors.textSecondary }}>
            Want to use your own templates?
          </p>
          <button
            onClick={() => navigate('/register')}
            style={{
              padding: `${spacing.md} ${spacing.xl}`,
              background: colors.primary,
              color: colors.onPrimary,
              border: 'none',
              borderRadius: radius.full,
              fontSize: fontSize.md,
              fontWeight: 600,
              cursor: 'pointer',
              boxShadow: shadow.md,
            }}
          >
            Start free trial
          </button>
        </div>
      </div>
  )
}
